<template>
  <div class="tag-mobile-container">
    <!-- 操作按钮区域，固定在右下角 -->
    <div class="mobile-action-buttons">
      <!-- 查询按钮 -->
      <el-button class="mobile-icon-btn" @click="showFilterDialog">
        <i class="el-icon-search"></i>
      </el-button>
      
      <!-- 全部标签按钮 -->
      <el-button 
        class="mobile-icon-btn" 
        :class="{ 'all-tags-active': showAllTags }"
        @click="handleAllTags"
      >
        <i class="el-icon-collection-tag"></i>
      </el-button>
    </div>
    
    <!-- 标题栏 -->
    <div class="mobile-header" :class="{ 'header-hidden': !headerVisible }">
      <h1 class="page-title">标签列表</h1>
    </div>
    
    <!-- 标签卡片区域 -->
    <div class="tag-cards-container" :class="{ 'header-hidden': !headerVisible }" v-loading="loading" @scroll="handleScroll">
      <div v-if="tagList.length === 0 && !loading" class="no-data">
        <div class="no-data-content">
          <i class="el-icon-collection-tag"></i>
          <span class="no-data-title">暂无标签</span>
          <p class="no-data-tips">
            没有找到符合条件的标签，请尝试调整筛选条件
          </p>
        </div>
      </div>
      
      <template v-else>
        <div 
          v-for="tag in displayTagList" 
          :key="tag.tag_code"
          class="tag-card"
          @click="handleCardClick(tag)"
        >
          <!-- 难度指示器（右上角三角形） -->
          <div class="difficulty-indicator">
            <div class="triangle-background"
                 :style="{ 
                   'border-color': `transparent ${getDifficultyColorByPercentage(tag.avg_difficulty)} transparent transparent` 
                 }">
            </div>
            <span class="difficulty-value">
              {{ formatDifficultyPercentage(tag.avg_difficulty) }}
            </span>
          </div>
          
          <!-- 标签基本信息 -->
          <div class="tag-card-main">
            <!-- 标签名称区域 -->
            <div class="tag-header">
              <h3 class="tag-name">{{ tag.tag_name }}</h3>
            </div>
            
            <div class="tag-info">
              <div class="tag-data-grid">
                <!-- 得分指标 -->
                <div class="tag-score">
                  <span class="score-label">平均分数：</span>
                  <div class="score-value-container">
                    <!-- 原始分数 -->
                    <span class="score-value" :style="{ 
                      color: getScoreColor(tag.question_score_avg),
                      fontWeight: 'bold'
                    }">
                      {{ tag.question_score_avg.toFixed(2) }}
                    </span>
                    <span class="score-separator">/</span>
                    <!-- 订正分数 -->
                    <span class="score-value" :style="{ 
                      color: getScoreColor(tag.question_score_fix_avg),
                      fontWeight: 'bold'
                    }">
                      {{ tag.question_score_fix_avg.toFixed(2) }}
                    </span>
                  </div>
                </div>
                
                <!-- 题目数量 -->
                <div class="tag-question-count">
                  <span class="count-label">题目数：</span>
                  <div class="count-value-container">
                    <span class="count-value pending-count">
                      {{ tag.pending_questions }}
                    </span>
                    <span class="count-separator">/</span>
                    <span class="count-value">
                      {{ tag.question_nums }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- 正确率与订正率进度条（左右并排） -->
              <div class="rate-row">
                <div class="rate-card">
                  <div class="rate-header">
                    <span class="rate-label">正确率：</span>
                    <span class="rate-value" :style="{ color: getScoreColor(tag.correct_rate), fontWeight: '600' }">
                      {{ tag.correct_rate.toFixed(2) }}%
                    </span>
                  </div>
                  <el-progress 
                    :percentage="tag.correct_rate" 
                    :show-text="false"
                    :stroke-width="5"
                    :color="getScoreColor(tag.correct_rate)">
                  </el-progress>
                </div>
                <div class="rate-card">
                  <div class="rate-header">
                    <span class="rate-label">订正率：</span>
                    <span class="rate-value" :style="{ color: getScoreColor(tag.fix_rate), fontWeight: '600' }">
                      {{ tag.fix_rate.toFixed(2) }}%
                    </span>
                  </div>
                  <el-progress 
                    :percentage="tag.fix_rate" 
                    :show-text="false"
                    :stroke-width="5"
                    :color="getScoreColor(tag.fix_rate)">
                  </el-progress>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      
      <!-- 加载更多 -->
      <div v-if="hasMore && !loading && displayTagList.length > 0" class="load-more">
        <el-button type="text" @click="loadMore">加载更多</el-button>
      </div>
      <div v-if="loading && displayTagList.length > 0" class="loading-more">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-if="!hasMore && displayTagList.length > 0" class="no-more">
        <span>------已经到底啦------</span>
        <div class="copyright-info">
          <span class="mobile-copyright">Copyright © 2024 OIer之路 All rights reserved.<br>闽ICP备2025095302号-1</span>
        </div>
      </div>
    </div>
    
    <!-- 自定义查询筛选弹窗 -->
    <div class="dialog-container" v-show="filterDialogVisible">
      <div class="dialog-mask" @click="filterDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="filterDialogVisible = false">×</div>
        <div class="dialog-body filter-dialog-body">
          <div class="dialog-content filter-dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title filter-title">查询条件</h3>
            <el-form :model="searchForm" class="filter-form" size="small" label-width="70px">
              <el-form-item label="标签名称">
                <el-input v-model="searchForm.tag_name" placeholder="请输入标签名称"></el-input>
              </el-form-item>
              
              <el-form-item label="时间范围">
                <div class="date-range-container">
                  <el-date-picker
                    v-model="searchForm.start_time"
                    type="date"
                    placeholder="开始日期"
                    value-format="yyyy-MM-dd"
                    size="small"
                    style="width: 100%;"
                    class="date-picker-item">
                  </el-date-picker>
                  <span class="date-separator">至</span>
                  <el-date-picker
                    v-model="searchForm.end_time"
                    type="date"
                    placeholder="结束日期"
                    value-format="yyyy-MM-dd"
                    size="small"
                    style="width: 100%;"
                    class="date-picker-item">
                  </el-date-picker>
                </div>
              </el-form-item>
            </el-form>
            <div class="dialog-footer filter-footer">
              <el-button size="small" @click="handleReset">重置</el-button>
              <el-button size="small" type="primary" @click="handleSearch">查询</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
        <!-- 标签详情弹窗 -->
    <div class="dialog-container" v-show="detailDialogVisible">
      <div class="dialog-mask" @click="closeDetailDialog"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="closeDetailDialog">×</div>
        <div class="dialog-body tag-detail-dialog-body">
          <div v-if="currentTag" class="tag-detail" style="background-color: #ffffff;">
            <!-- 固定头部区域 - 不会随滚动而移动 -->
            <div class="detail-fixed-header">
              <h3 class="tag-title">{{ currentTag.tag_name }}</h3>
              
              <!-- 标签详情指标卡片 - 两行三列布局 -->
              <div class="tag-stats-card">
                <!-- 第一行：平均分数、平均订正分、平均难度 -->
                <div class="tag-stats-row">
                  <div class="tag-stat-item">
                    <span class="stat-label">平均分数</span>
                    <span class="stat-value" :style="{ color: getScoreColor(currentTag.question_score_avg), fontWeight: 'bold' }">
                      {{ currentTag.question_score_avg.toFixed(2) }}
                    </span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">平均订正分</span>
                    <span class="stat-value" :style="{ color: getScoreColor(currentTag.question_score_fix_avg), fontWeight: 'bold' }">
                      {{ currentTag.question_score_fix_avg.toFixed(2) }}
                    </span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">平均难度</span>
                    <span class="stat-value difficulty-badge" 
                          :style="{ backgroundColor: getDifficultyColorByPercentage(currentTag.avg_difficulty) }">
                      {{ formatDifficultyPercentage(currentTag.avg_difficulty) }}
                    </span>
                  </div>
                </div>
                
                <!-- 第二行：题目数、未订正题目数、订正率 -->
                <div class="tag-stats-row">
                  <div class="tag-stat-item">
                    <span class="stat-label">题目数</span>
                    <span class="stat-value">{{ currentTag.question_nums }}</span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">未订正题目数</span>
                    <span class="stat-value">{{ currentTag.pending_questions }}</span>
                  </div>
                  <div class="tag-stat-item">
                    <span class="stat-label">订正率</span>
                    <span class="stat-value" :style="{ color: getScoreColor(currentTag.fix_rate), fontWeight: 'bold' }">
                      {{ currentTag.fix_rate.toFixed(2) }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 可滚动内容区域 -->
            <div class="detail-scrollable-content" @scroll="handleQuestionScroll">
              <div v-if="displayQuestionList.length > 0" class="question-list">
                <div 
                  v-for="question in displayQuestionList" 
                  :key="question.unique_id || question.question_code" 
                  class="question-item"
                  :class="{ 'clickable-replay': question.has_replay }"
                  @click="handleQuestionCardClick(question, $event)">
                  <!-- 订正印章 -->
                  <div v-if="question.question_score === 100 || (question.question_score < 100 && question.question_score_fix === 100)" class="question-fix-stamp">
                    <div class="stamp-content" :class="{ 'ak-stamp': question.question_score === 100 }">
                      {{ question.question_score === 100 ? 'AC' : '订' }}
                    </div>
                  </div>
                  
                  <div class="question-header">
                    <span class="question-seq" :style="{ backgroundColor: getDifficultyColor(question.question_level || '未定义'), color: 'white' }">{{ question.question_seq }}</span>
                    <span class="question-name">
                      <a v-if="question.question_url" 
                         :href="question.question_url" 
                         target="_blank" 
                         rel="noopener"
                         class="question-name-link">
                        {{ question.question_name }}
                      </a>
                      <span v-else>{{ question.question_name }}</span>
                      <i
                        v-if="question.has_replay"
                        class="el-icon-video-camera replay-icon-badge"
                        :title="'查看复盘'"
                        style="margin-left: 2px;">
                      </i>
                    </span>
                  </div>
                  
                  <div class="question-info">
                    <!-- 赛事名称 -->
                    <div class="game-name-row">
                      <i class="el-icon-trophy"></i>
                      <span>{{ question.game_name }}</span>
                    </div>
                    
                    <!-- 得分信息 -->
                    <div class="score-row">
                      <div class="score-cell">
                        <span class="score-label">得分：</span>
                        <i v-if="question.question_score === 100" 
                           class="el-icon-medal-1" 
                           :style="{ color: getScoreColor(question.question_score), marginRight: '4px' }"></i>
                        <span class="score-value" :style="{ 
                          color: getScoreColor(question.question_score),
                          fontWeight: 'bold'
                        }">
                          {{ question.question_score }}
                        </span>
                      </div>
                      
                      <div class="score-cell">
                        <span class="score-label">订正：</span>
                        <i v-if="question.question_score_fix === 100" 
                           class="el-icon-medal-1" 
                           :style="{ color: getScoreColor(question.question_score_fix), marginRight: '4px' }"></i>
                        <span class="score-value" :style="{ 
                          color: getScoreColor(question.question_score_fix),
                          fontWeight: 'bold'
                        }">
                          {{ question.question_score_fix }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 错误类型 chips（位于分数下面一行），仅在存在错误类型时显示 -->
                  <div
                    v-if="normalizeErrors(question.errors).length > 0"
                    class="error-types-row"
                    style="margin-top: 8px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center;">
                    <el-tag
                      v-for="err in normalizeErrors(question.errors)"
                      :key="(err.error_code || err.error_name || err) + '-m'"
                      size="mini"
                      :style="getErrorOutlineStyle(err.error_code || err.error_name)"
                      style="border-width: 1px;">
                      <i class="el-icon-warning" style="margin-right: 3px;"></i>{{ err.error_name || err }}
                    </el-tag>
                  </div>
                </div>
              </div>
              <div v-else class="no-questions">
                <i class="el-icon-document"></i>
                <span>暂无题目信息</span>
              </div>
              
              <!-- 加载状态和底部提示 -->
              <div v-if="questionLoading && displayQuestionList.length > 0" class="loading-more">
                <i class="el-icon-loading"></i>
                <span>加载中...</span>
              </div>
              <div v-if="!hasMoreQuestions && displayQuestionList.length > 0" class="question-no-more">
                <span>------已经到底啦------</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 复盘弹窗（移动端） -->
    <div class="dialog-container" v-show="replayDialogVisible">
      <div class="dialog-mask" @click="replayDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="replayDialogVisible = false">×</div>
        <div class="dialog-body" style="background-color: #ffffff;">
          <h3 class="dialog-custom-title" style="margin-top: 0;">{{ replayQuestionTitle ? `${replayQuestionTitle} - 复盘内容` : '复盘内容' }}</h3>
          <div v-loading="replayLoading">
            <markdown-viewer v-if="replayContent" :content="replayContent" />
            <div v-else style="color:#909399;">暂无复盘内容</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template> 

<script>
import { getTagsStatistics, getTagQuestions } from '@/api/tag'
import { getQuestionReplay } from '@/api/question'
import MarkdownViewer from '@/components/MarkdownViewer.vue'
import { getErrorOutlineStyle } from '@/constants/errorTypes'

export default {
  name: 'TagMobile',
  components: { MarkdownViewer },
  data() {
    return {
      loading: false,
      questionLoading: false,
      tagList: [],
      questionList: [], // 当前显示的题目数据
      
      // 搜索表单
      searchForm: {
        tag_name: '',
        start_time: '',
        end_time: ''
      },
      
      // 弹窗控制
      filterDialogVisible: false,
      detailDialogVisible: false,
      
      // 当前选中的标签
      currentTag: null,
      
      // 分页相关
      currentPage: 1,
      pageSize: 20,
      total: 0,
      hasMore: true,
      
      // 是否显示全部标签
      showAllTags: false,
      
      // 题目列表瀑布流相关
      questionPage: 1,
      questionPageSize: 20,
      questionTotal: 0,
      hasMoreQuestions: true,
      
      // 防止重复加载的标志
      _loadingMore: false,
      _loadingMoreQuestions: false,

      // 滚动状态控制
      lastScrollTop: 0,
      headerVisible: true,
      scrollThreshold: 10, // 滚动阈值，避免微小滚动触发
      // 复盘弹窗
      replayDialogVisible: false,
      replayLoading: false,
      replayContent: '',
      replayQuestionTitle: ''
    }
  },
  created() {
    this.fetchTagList()
  },
  computed: {
    // 计算当前页应该显示的标签数据
    displayTagList() {
      const start = 0
      const end = this.currentPage * this.pageSize
      return this.tagList.slice(start, end)
    },
    
    // 计算当前页应该显示的题目数据
    displayQuestionList() {
      const start = 0
      const end = this.questionPage * this.questionPageSize
      return this.questionList.slice(start, end)
    }
  },
  methods: {
    handleQuestionCardClick(question, evt) {
      try {
        // 如果点击的是链接本身，放行默认行为
        const path = evt.composedPath ? evt.composedPath() : (evt.path || [])
        const clickedLink = path.find && path.find(el => el && el.tagName === 'A')
        if (clickedLink) return
        if (question && question.has_replay) {
          this.openReplay(question)
        }
      } catch (_) {
        if (question && question.has_replay) {
          this.openReplay(question)
        }
      }
    },
    async openReplay(question) {
      if (!question || !question.question_code) return
      this.replayDialogVisible = true
      this.replayLoading = true
      this.replayContent = ''
      this.replayQuestionTitle = question.question_name || ''
      try {
        const resp = await getQuestionReplay(question.question_code)
        if (resp && resp.code === 200) {
          this.replayContent = resp.data || ''
        } else {
          this.$message.error('获取复盘失败')
          this.replayDialogVisible = false
        }
      } catch (e) {
        this.$message.error('获取复盘失败')
        this.replayDialogVisible = false
      } finally {
        this.replayLoading = false
      }
    },
    normalizeErrors(errors) {
      if (!errors) return []
      if (Array.isArray(errors)) return errors
      try {
        const arr = JSON.parse(errors)
        return Array.isArray(arr) ? arr : []
      } catch (e) { return [] }
    },
    getErrorOutlineStyle,
    // 获取标签列表 - 前端分页实现
    async fetchTagList() {
      if (this.loading) return // 防止重复请求
      
      this.loading = true
      try {
        // 构建请求参数（获取所有数据）
        const params = {
          tag_name: this.searchForm.tag_name,
          start_time: this.searchForm.start_time,
          end_time: this.searchForm.end_time,
          show_all: this.showAllTags
        }
        
        const response = await getTagsStatistics(params)
        
        if (response && response.code === 200) {
          this.tagList = response.data || []
          
          // 计算总数和是否还有更多数据
          this.total = this.tagList.length
          this.hasMore = this.currentPage * this.pageSize < this.total
          
          console.log(`标签加载结果: 当前列表长度=${this.tagList.length}, 总数据=${this.total}, 还有更多=${this.hasMore}`)
        } else {
          this.tagList = []
          this.total = 0
          this.hasMore = false
        }
      } catch (error) {
        console.error('获取标签列表失败:', error)
        this.$message.error('获取标签列表失败')
        this.tagList = []
        this.total = 0
        this.hasMore = false
      } finally {
        this.loading = false
      }
    },
    
    // 获取标签关联的题目列表 - 前端分页实现
    async fetchQuestionList(tagCode) {
      if (this.questionLoading) return
      
      this.questionLoading = true
      
      // 重置分页参数
      this.questionPage = 1
      this.questionList = []
      
      try {
        // 构建请求参数（获取所有数据）
        const params = {
          start_time: this.searchForm.start_time,
          end_time: this.searchForm.end_time
        }
        
        const response = await getTagQuestions(tagCode, params)
        
        if (response && response.code === 200) {
          const allData = response.data || []
          
          // 确保每条数据有唯一键
          this.questionList = allData.map((item, index) => ({
            ...item,
            unique_id: `${tagCode}_${index}_${Date.now()}`
          }))
          
          // 计算总数和是否还有更多数据
          this.questionTotal = this.questionList.length
          this.hasMoreQuestions = this.questionPage * this.questionPageSize < this.questionTotal
          
          console.log(`题目加载结果: 当前列表长度=${this.questionList.length}, 总数据=${this.questionTotal}, 还有更多=${this.hasMoreQuestions}`)
        } else {
          this.questionList = []
          this.questionTotal = 0
          this.hasMoreQuestions = false
        }
      } catch (error) {
        console.error('获取题目列表失败:', error)
        this.$message.error('获取题目列表失败')
        this.questionList = []
        this.questionTotal = 0
        this.hasMoreQuestions = false
      } finally {
        this.questionLoading = false
      }
    },
    
    // 这个方法不再需要，因为现在我们直接从API获取分页数据
    // 保留一个空方法以防其他地方调用
    updateQuestionDisplayData() {
      console.log('updateQuestionDisplayData 方法已弃用，现在使用直接分页请求数据')
    },
    
    // 加载更多题目 - 前端分页实现
    async loadMoreQuestions() {
      // 简单检查：如果没有更多数据或正在加载，直接返回
      if (!this.hasMoreQuestions || this.questionLoading) return
      
      // 增加页码
      this.questionPage++
      
      // 前端分页：计算当前应该显示的数据
      const start = (this.questionPage - 1) * this.questionPageSize
      const end = start + this.questionPageSize
      
      // 检查是否还有更多数据可以显示
      this.hasMoreQuestions = end < this.questionList.length
      
      // 加载完成后重置标志，允许下次触发
      this._loadingMoreQuestions = false
    },
    
    // 关闭详情弹窗并清理状态
    closeDetailDialog() {
      this.detailDialogVisible = false
      
      // 清理所有相关状态
      if (this._scrollTimer) {
        clearTimeout(this._scrollTimer)
        this._scrollTimer = null
      }
      
      // 重置相关数据
      this.questionPage = 1
      this.questionList = []
      this.hasMoreQuestions = true
      this.questionTotal = 0
      
      // 重置加载标志
      this._loadingMore = false
      this._loadingMoreQuestions = false
    },
    
    // 显示筛选弹窗
    showFilterDialog() {
      this.filterDialogVisible = true
    },
    
    // 处理全部标签
    handleAllTags() {
      this.showAllTags = !this.showAllTags
      this.currentPage = 1
      this.tagList = []
      this.total = 0
      this.hasMore = true
      this._loadingMore = false
      this.fetchTagList()
    },
    
    // 处理卡片点击
    handleCardClick(tag) {
      // 设置当前标签并显示弹窗
      this.currentTag = tag
      this.detailDialogVisible = true
      
      // 重置分页相关状态
      this.questionPage = 1 
      this.questionList = []
      this.hasMoreQuestions = true
      this.questionTotal = 0
      this._loadingMoreQuestions = false
      
      // 清除任何现有的计时器
      if (this._scrollTimer) {
        clearTimeout(this._scrollTimer)
        this._scrollTimer = null
      }
      
      // 获取题目数据
      this.fetchQuestionList(tag.tag_code)
    },
    
    // 处理搜索
    handleSearch() {
      this.currentPage = 1
      this.tagList = []
      this.total = 0
      this.hasMore = true
      this._loadingMore = false
      this.fetchTagList()
      this.filterDialogVisible = false
    },
    
    // 处理重置
    handleReset() {
      this.searchForm = {
        tag_name: '',
        start_time: '',
        end_time: ''
      }
    },
    
    // 加载更多 - 前端分页实现
    async loadMore() {
      if (!this.hasMore || this.loading) return
      
      this.currentPage++
      
      // 前端分页：计算当前应该显示的数据
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      
      // 检查是否还有更多数据可以显示
      this.hasMore = end < this.tagList.length
      
      // 加载完成后重置标志，允许下次触发
      this._loadingMore = false
    },
    
    // 处理滚动事件，实现自动加载更多 - 防止循环触发
    handleScroll(event) {
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      
      // 检测滚动方向并控制头部横幅显示
      const deltaY = scrollTop - this.lastScrollTop
      
      if (Math.abs(deltaY) > this.scrollThreshold) {
        if (deltaY > 0) {
          // 向下滚动（内容向上移动），隐藏头部横幅
          this.headerVisible = false
        } else {
          // 向上滚动（内容向下移动），显示头部横幅
          this.headerVisible = true
        }
      }
      
      this.lastScrollTop = scrollTop
      
      // 当滚动到距离底部100px时，加载更多内容（仅在满足条件时）
      if (!this.loading && this.hasMore && scrollHeight - scrollTop - clientHeight < 100) {
        // 防止重复触发：检查是否已经在加载更多
        if (!this._loadingMore) {
          this._loadingMore = true
          this.loadMore()
        }
      }
    },
    
    // 题目区域滚动处理 - 防止循环触发
    handleQuestionScroll(event) {
      // 基础检查：如果加载中或没有更多数据，直接返回
      if (this.questionLoading || !this.hasMoreQuestions) return
      
      // 获取滚动信息
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      
      // 当滚动到距离底部30px时，调用加载更多
      if (scrollHeight - scrollTop - clientHeight < 30) {
        // 防止重复触发：检查是否已经在加载更多
        if (!this._loadingMoreQuestions) {
          this._loadingMoreQuestions = true
          this.loadMoreQuestions()
        }
      }
    },
    
    // 格式化难度百分比
    formatDifficultyPercentage(value) {
      if (value === null || value === undefined) return '0.00%'
      return value.toFixed(2) + '%'
    },
    
    // 获取难度颜色（根据百分比区间确定）
    getDifficultyColorByPercentage(percentage) {
      if (percentage === null || percentage === undefined) return '#909399' // 默认灰色
      
      if (percentage < 14.29) return '#F56C6C' // 红题
      if (percentage < 28.58) return '#E6A23C' // 橙题
      if (percentage < 42.87) return '#F7BA2A' // 黄题
      if (percentage < 57.15) return '#67C23A' // 绿题
      if (percentage < 71.44) return '#409EFF' // 蓝题
      if (percentage < 85.72) return '#8E44AD' // 紫题
      return '#2C3E50' // 黑题
    },
    
    // 获取难度颜色（与 Question.vue 保持一致）
    getDifficultyColor(level) {
      const colorMap = {
        '红题': '#F56C6C',  // 红色
        '橙题': '#E6A23C',  // 橙色
        '黄题': '#F7BA2A',  // 黄色
        '绿题': '#67C23A',  // 绿色
        '蓝题': '#409EFF',  // 蓝色
        '紫题': '#8E44AD',  // 紫色
        '黑题': '#2C3E50',  // 黑色
        '': '#909399'       // 灰色
      }
      return colorMap[level] || '#909399'
    },
    
    // 获取分数颜色
    getScoreColor(score) {
      if (score === undefined || score === null) return '#606266'
      const percentage = score
      
      if (percentage >= 100) return '#237804' // 绿色
      if (percentage >= 75) return '#52C41A'  // 黄绿色
      if (percentage >= 50) return '#F7BA2A'  // 黄色
      if (percentage >= 25) return '#E6A23C'  // 橙色
      return '#F56C6C' // 红色
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-mobile-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  position: relative;
  overflow: hidden;
  width: 100%;
  margin: 0 auto;
}

/* 移动端操作按钮 - 与GameMobile.vue中全局菜单按钮样式保持一致 */
.mobile-action-buttons {
  position: fixed;
  right: 15px;
  bottom: 135px; /* 距离底部的距离 */
  display: flex;
  flex-direction: column;
  gap: 15px; /* 按钮之间的垂直间距 */
  z-index: 2001;
  width: 40px; /* 限制容器宽度与按钮一致 */
  align-items: center; /* 确保按钮水平居中对齐 */
  padding: 0 !important;
  margin: 0 !important;
  
  /* 确保每个按钮容器没有额外的边距和内边距 */
  .el-button {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }
}

.mobile-icon-btn {
  width: 40px !important; /* 与GameMobile.vue保持一致 */
  height: 40px !important; /* 与GameMobile.vue保持一致 */
  border-radius: 40px !important; /* 与GameMobile.vue保持一致 */
  background-color: rgba(255, 255, 255, 0.5) !important; /* 与GameMobile.vue保持一致 */
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important; /* 确保没有外边距干扰对齐 */
  transition: all 0.3s ease !important;
  position: relative !important; /* 使用相对定位 */
  left: 0 !important; /* 确保没有左偏移 */
  
  i {
    font-size: 20px !important; /* 与GameMobile.vue保持一致 */
    margin: 0 !important; /* 确保图标没有外边距 */
    padding: 0 !important; /* 确保图标没有内边距 */
    display: inline-flex !important; /* 使用inline-flex布局 */
    justify-content: center !important; /* 水平居中 */
    align-items: center !important; /* 垂直居中 */
    width: 20px !important; /* 固定宽度 */
    height: 20px !important; /* 固定高度 */
  }
  
  &:hover, &:focus {
    background-color: rgba(255, 255, 255, 0.8) !important; /* 与GameMobile.vue保持一致 */
    transform: scale(1.05) !important;
  }
  
  &:active {
    background-color: rgba(255, 255, 255, 0.9) !important; /* 与GameMobile.vue保持一致 */
    transform: scale(0.95) !important;
  }
  
  /* 全部标签按钮激活状态样式 */
  &.all-tags-active {
    background-color: rgba(64, 158, 255, 0.5) !important; /* 蓝色背景，保持透明度 */
    
    i {
      color: #ffffff !important; /* 图标变成白色，与蓝色背景形成对比 */
    }
    
    &:hover, &:focus {
      background-color: rgba(64, 158, 255, 0.7) !important; /* hover 时稍微深一点 */
    }
    
    &:active {
      background-color: rgba(64, 158, 255, 0.8) !important; /* active 时更深 */
    }
  }
}

/* 标题栏 */
.mobile-header {
  height: 50px;
  background-color: #304156; /* 与GameMobile.vue中的.mobile-header背景色保持一致 */
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent); /* 添加与抽屉菜单相同的渐变效果 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08); /* 添加与抽屉菜单相同的底部边框 */
  transition: all 0.3s ease;
  
  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }
  
  /* 隐藏状态 */
  &.header-hidden {
    transform: translateY(-100%);
    opacity: 0;
  }
}

/* 标签卡片容器 */
.tag-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 20px;
  padding-top: 60px; /* 为固定定位的头部预留空间 */
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  margin: 0 auto;
  transition: padding-top 0.3s ease; /* 添加顶部内边距的过渡动画 */
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* 当头部隐藏时，减少顶部内边距 */
  &.header-hidden {
    padding-top: 10px;
  }
  
  /* 标签卡片 */
  .tag-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    margin-bottom: 14px;
    overflow: hidden;
    transition: all 0.3s ease;
    width: 100%;
    display: flex; /* 使用flex布局 */
    align-items: flex-start; /* 对齐卡片顶部 */
    position: relative; /* 相对定位 */
    border: 1px solid #ebeef5;
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
    
    &:last-child {
      margin-bottom: 20px; /* 最后一张卡片底部增加间距 */
    }
    
    /* 难度指示器 - 右上角三角形 */
    .difficulty-indicator {
      position: absolute;
      top: 0;
      right: 0;
      width: 60px;
      height: 60px;
      overflow: hidden;
      z-index: 1;
      pointer-events: none; /* 确保点击事件透过指示器 */
      
      .triangle-background {
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 60px 60px 0;
        opacity: 0.85; /* 稍微降低不透明度，使其更和谐 */
      }
      
      .difficulty-value {
        position: absolute;
        /* 调整位置确保完全居中显示 */
        top: 12px;
        right: 12px;
        /* 使文本平行于斜边 */
        transform: rotate(45deg);
        color: white;
        font-size: 12px;
        font-weight: bold;
        text-align: center;
        white-space: nowrap;
        width: 30px; /* 调整宽度确保文本居中 */
        line-height: 1;
        text-shadow: 0 1px 1px rgba(0,0,0,0.3);
        letter-spacing: 0px; /* 略微调整字间距使百分比更紧凑 */
      }
    }

    .tag-card-main {
      padding: 15px;
      padding-top: 10px;
      flex: 1; /* 占据剩余空间 */
      width: 100%; /* 确保内容区域占满宽度 */
      
      .tag-header {
        display: flex;
        align-items: flex-start; /* 顶部对齐 */
        margin-bottom: 0px;
        padding-right: 50px; /* 为右上角三角形留出更多空间 */
        min-height: 30px; /* 确保即使名称短也有足够空间 */
        
        .tag-name {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
          line-height: 1.4;
          word-break: break-word;
          margin: 0; /* 移除默认margin */
          padding: 0; /* 移除默认padding */
          flex: 1; /* 让标签名称占据更多空间 */
          display: -webkit-box;
          -webkit-line-clamp: 2; /* 最多显示2行 */
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      
      .tag-info {
        margin-top: 0px;
        border-top: 1px solid #f5f5f5;
        padding-top: 10px;
        
        .tag-data-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 8px;
          margin-bottom: 12px;
        }
        
        .tag-score,
        .tag-question-count {
          font-size: 12px;
          display: flex;
          align-items: center;
          background-color: rgba(249, 250, 252, 0.8);
          border-radius: 6px;
          padding: 6px 10px;
          border: 1px solid #f0f2f5;
          
          .score-label,
          .count-label {
            color: #606266;
            font-weight: 500;
            white-space: nowrap;
          }
          
          .score-value-container,
          .count-value-container {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            gap: 3px;
            flex: 1;
            justify-content: flex-start;
            margin-left: 4px;
          }
          
          .score-value,
          .count-value {
            font-weight: 600;
            color: #303133;
          }
          
          .fix-score {
            color: #67C23A; /* 绿色显示订正分数 */
          }
          
          .pending-count {
            color: #F56C6C; /* 红色显示未订正题目数 */
          }
          
          .score-separator,
          .count-separator {
            color: #c0c4cc;
            font-weight: 400;
            padding: 0 1px;
          }
          
          .count-hint {
            font-size: 10px;
            color: #909399;
            margin-left: 2px;
          }
        }
        
        /* 正确率与订正率容器（左右布局） */
        .rate-row {
          margin-top: 8px;
          padding: 0 2px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-gap: 8px;
        }

        .rate-card {
          background-color: transparent;
          border: none;
          border-radius: 0;
          padding: 0 2px;
        }

        .rate-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .rate-label {
          font-size: 12px;
          color: #606266;
          font-weight: 500;
        }

        .rate-value {
          font-size: 12px;
          font-weight: 600;
        }

        ::v-deep .el-progress-bar__outer {
          background-color: #f0f2f5;
          border-radius: 4px;
        }
        
        ::v-deep .el-progress-bar__inner {
          border-radius: 4px;
          transition: width 0.6s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
      }
    }
  }
  
  /* 暂无数据样式 */
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    
    .no-data-content {
      text-align: center;
      padding: 30px;
      
      i {
        font-size: 50px;
        color: #dcdfe6;
        margin-bottom: 20px;
      }
      
      .no-data-title {
        font-size: 18px;
        color: #606266;
        font-weight: 500;
        margin-bottom: 10px;
        display: block;
      }
      
      .no-data-tips {
        font-size: 14px;
        color: #909399;
      }
    }
  }
  
  /* 加载更多区域 */
  .load-more, .loading-more, .no-more {
    text-align: center;
    margin-top: 10px;
    padding: 10px 0;
    
    .el-button {
      font-size: 14px;
    }
  }
  
  .loading-more {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    color: #909399;
    
    i {
      font-size: 16px;
    }
  }
  
  .no-more {
    text-align: center;
    padding: 15px 0 20px;
    
    span {
      color: #999999;
      font-size: 12px;
      display: inline-block;
      position: relative;
      font-weight: 400;
      letter-spacing: 0.5px;
    }
    
    .copyright-info {
      text-align: center;
      width: 100%;
      margin: 15px auto 0;
      display: block;
      padding: 0 15px 15px;
      box-sizing: border-box;
      
      .mobile-copyright {
        text-align: center;
        width: 100%;
        color: #909399;
        font-size: 12px;
        line-height: 1.5;
      }
    }
  }
}

/* 标签详情弹窗 */
.tag-detail {
  .detail-header {
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 15px;
    margin-bottom: 15px;
    position: relative;
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 15px 0;
      word-break: break-word;
    }
  }
  
  /* 标签详情指标卡片 - 两行三列布局 */
  .tag-stats-card {
    margin-top: 12px;
    
    .tag-stats-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-bottom: 8px;
    }
    
    .tag-stat-item {
      background-color: #f9fafc;
      border-radius: 6px;
      padding: 8px 5px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      
      .stat-label {
        font-size: 12px;
        color: #909399;
        margin-bottom: 4px;
        text-align: center;
      }
      
      .stat-value {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        text-align: center;
        
        &.difficulty-badge {
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
        }
      }
    }
  }
  
  /* 固定头部样式 */
  .detail-fixed-header {
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 15px;
    margin-bottom: 15px;
    position: sticky;
    top: 0;
    background-color: #ffffff;
    z-index: 10;
    
    .tag-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 15px 0;
      padding-right: 35px; /* 为关闭按钮留出更多空间 */
      word-break: break-word;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  /* 可滚动内容区域样式 */
  .detail-scrollable-content {
    overflow-y: auto;
    flex: 1; /* 让内容区域自动填充剩余空间 */
    min-height: 200px; /* 确保小数据量时也有滚动区域 */
    max-height: calc(80vh - 220px); /* 调整高度确保可看到底部内容 */
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 20px; /* 确保底部有足够空间 */
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    /* 底部"已经到底啦"样式 */
    .question-no-more {
      text-align: center;
      padding: 15px 0 25px;
      width: 100%;
      margin-top: 5px; /* 增加顶部间距 */
      
      span {
        color: #999999;
        font-size: 12px;
        line-height: 1.2;
        font-weight: 400;
        position: relative;
        display: inline-block;
        padding: 0 10px; /* 增加左右内边距 */
      }
    }
    
    .question-list {
      .question-item {
        background-color: #f9fafc !important;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 10px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
        border: 1px solid #ebeef5;
        position: relative; /* 为印章定位做准备 */
        
        /* 题目订正印章 */
        .question-fix-stamp {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 2; /* 确保印章在内容之上 */
          
          .stamp-content {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background-color: rgba(103, 194, 58, 0.6); /* 绿色 - 订正，增加透明度 */
            color: white;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 14px;
            font-weight: bold;
            border: 1.5px solid rgba(255, 255, 255, 0.6);
            box-shadow: 0 0 5px rgba(0, 0, 0, 0.15);
            text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
            transform: rotate(-15deg);
            
            /* AC印章的样式 */
            &.ak-stamp {
              background-color: rgba(247, 186, 42, 0.6); /* 黄色 - AC，增加透明度 */
            }
          }
        }
        
        .question-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
          
          .question-seq {
            color: #ffffff;
            font-size: 12px;
            padding: 2px 8px;
            border-radius: 4px;
            margin-right: 10px;
            flex-shrink: 0;
            box-shadow: 0 1px 2px rgba(0,0,0,0.08);
          }
          
          .question-name {
            font-size: 14px;
            font-weight: 500;
            color: #303133;
            flex: 1;
            line-height: 1.4;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            
            .question-name-link {
              color: #409EFF;
              text-decoration: none;
              transition: color 0.2s ease;
              
              &:hover {
                color: #66b1ff;
                text-decoration: underline;
              }
            }

            /* 复盘相机徽标样式 */
            .replay-icon-badge {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: rgba(64, 158, 255, 0.15); /* 浅蓝色圆形背景 */
              color: #409EFF; /* 相机图标为蓝色 */
              margin-left: 2px;
              box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.06);
            }
          }
        }
        
        .question-info {
          .game-name-row {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
            font-size: 13px;
            color: #606266;
            
            i {
              color: #E6A23C;
              margin-right: 5px;
              font-size: 14px;
            }
            
            span {
              flex: 1;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
          
          .score-row {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;
            
            .score-cell {
              display: flex;
              align-items: center;
              font-size: 13px;
              
              .score-label {
                color: #606266;
                font-weight: normal;
                margin-right: 4px;
              }
              
              i {
                font-size: 16px;
              }
              
              .score-value {
                font-weight: 600;
              }
            }
          }
        }
      }
    }
    
    .no-questions {
      text-align: center;
      padding: 20px 0;
      color: #909399;
      
      i {
        font-size: 24px;
        margin-bottom: 10px;
        display: block;
      }
    }
    
    .question-pagination {
      margin-top: 20px;
      display: flex;
      justify-content: center;
    }
  }
}

/* 自定义弹窗样式 */
.dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  justify-content: center;
}

.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 2001;
}

.custom-dialog {
  position: absolute;
  z-index: 2002;
  width: 95%;
  max-height: 80vh;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  left: 50%;
  transform: translateX(-50%);
  margin: 0 auto;
}

.dialog-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.15);
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 20; /* 增加z-index值确保在最上层 */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dialog-body {
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  &.filter-dialog-body {
    padding: 15px 15px 10px;
  }
  
  &.tag-detail-dialog-body {
    overflow-y: hidden; /* 防止整个dialog-body滚动 */
    display: flex;
    flex-direction: column;
    padding: 15px 15px 0;
    height: 80vh; /* 固定高度确保内部滚动正常工作 */
    
    .tag-detail {
      display: flex;
      flex-direction: column;
      height: 100%; /* 填充整个高度 */
    }
  }
}

/* 查询弹窗专用样式 */
.filter-dialog-content {
  padding: 0;
  
  .filter-title {
    font-size: 16px;
    margin: 0 0 10px 0;
    padding-bottom: 8px;
  }
}

.filter-form {
  .el-form-item {
    margin-bottom: 10px;
    
    .el-form-item__label {
      padding-right: 5px;
      line-height: 32px;
    }
    
    .el-form-item__content {
      line-height: 32px;
    }
  }
}

.filter-footer {
  margin-top: 10px;
  padding-top: 8px;
  text-align: right;
  
  .el-button {
    padding: 7px 15px;
    
    &+.el-button {
      margin-left: 8px;
    }
  }
}

/* 滚动容器样式 */
.detail-scroll-container {
  max-height: calc(80vh - 40px);
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

/* 日期选择器优化样式 */
.date-range-container {
  display: flex;
  align-items: center;
  gap: 2px;  /* 减少间距 */
  width: 100%;

  .date-picker-item {
    flex: 1;
  }
  
  .date-separator {
    flex-shrink: 0;
    color: #909399;
    padding: 0 1px;
    font-size: 12px;
  }
}
</style>

<!-- 全局样式：日期选择器优化 -->
<style>
/* 全局样式 - 不使用scoped，以便能够影响body下的元素 */
.el-picker-panel {
  left: 50% !important;
  transform: translateX(-50%) !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  z-index: 3000 !important;
}

/* 隐藏日期选择器箭头 */
.el-picker-panel .popper__arrow {
  display: none !important;
}

/* 优化日期选择器内容区域的padding，增加可视区域 */
.el-picker-panel .el-date-picker__body {
  padding: 10px 8px !important;
}

/* 优化日期表格单元格间距 */
.el-picker-panel .el-date-table th,
.el-picker-panel .el-date-table td {
  padding: 2px !important;
  width: 30px !important;
  height: 30px !important;
}

/* 优化日期选择器头部间距 */
.el-picker-panel .el-date-picker__header {
  margin: 6px 0 !important;
  padding: 0 8px !important;
}

/* 确保月份和年份选择下拉菜单不会被截断 */
.el-picker-panel .el-date-picker__header-label {
  font-size: 14px !important;
  padding: 0 2px !important;
}
</style>